<template>

  <v-container fluid class="page-content location-license-change-list-table">

    <v-row class="mb-3">
      <v-col class="pt-2">
        <div class="page-title">
          <v-icon class="pr-1">mdi-history</v-icon>
          License Change History
        </div>
      </v-col>
    </v-row>

    <v-data-table
        :headers="tableHeaders"
        :items="changeItems"
        :options.sync="dataOptions"
        :footer-props="paginationOptions"
        :loading="isDataLoading"
        :server-items-length="totalItems">

      <!-- location name -->
      <template v-slot:[`item.model`]="{ item }">
        <span style="font-size:1.14em">
          <router-link :to="generateLocationPageLink(item[locationType])">
            {{ item[locationType].name }}
          </router-link>
        </span>
      </template>

      <!-- change date (approval date) -->
      <template v-slot:item.approved_licenses_record.approved_at="{ item }">
        <span>{{ formatDate(item.approved_licenses_record.approved_at) }}</span>
      </template>

      <!-- dismiss date/action -->
      <template v-slot:[`item.is_dismissed`]="{ item }">
        <div v-if="item.is_dismissed">
          {{ formatDate(item.dismissed_at) }}
        </div>
        <template v-else>
          <v-btn outlined height="30" color="amber darken-3" @click="dismissNotification(item)">
            <span style="font-size:0.84rem;">Dismiss</span>
          </v-btn>
          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                  color="grey lighten-1"
                  style="font-size:22px;cursor:pointer;margin-left:8px"
                  v-bind="attrs"
                  v-on="on">
                mdi-information-outline
              </v-icon>
            </template>
            <div>Will dismiss notifications for your entire Org.</div>
          </v-tooltip>
        </template>
        <!-- <dialog-confirm-button
            :message="generateDismissConfirmationMessage(item)"
            buttonStyle="plain"
            icon="mdi-trash-can"
            iconColor="grey darken-1"
            v-on:confirmed="dismissNotification(item)" /> -->
        </template>
    </v-data-table>

  </v-container>

</template>

<script>
// import DialogConfirmButton from '@/components/app/DialogConfirmButton'

export default {
  name: 'LocationLicenseChangeListDataTable',

  components: {
    // DialogConfirmButton
  },

  props: {
    // The location type (shipper || receiver)
    locationType: {
      type: String,
      required: true
    },
    // Enable "dismiss" column and action
    showDismiss: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      isDataLoading: false,
      changeData: null,
      dataLoadingError: null,
      dismissError: null,
      paginationOptions: {
        'items-per-page-options': [10, 25, 50, 100]
      },
      dataOptions: {
        itemsPerPage: 10,
        // NOTE - Currently enabled for single field sort only !!!
        sortBy: ['approved_licenses_record.approved_at'],
        sortDesc: [true]
      }
    }
  },

  computed: {
    tableHeaders () {
      return (this.showDismiss)
        ? [
          { text: 'Location', sortable: false, value: 'model' },
          { text: 'Date Changed', value: 'approved_licenses_record.approved_at' },
          { text: 'Date dismissed', align: 'center', value: 'is_dismissed' }
        ]
        : [
          { text: 'Location', sortable: false, value: 'model' },
          { text: 'Date Changed', value: 'approved_licenses_record.approved_at' }
        ]
    },
    changeItems () {
      return (this.changeData) ? this.changeData.items : []
    },
    dataMeta () {
      return (this.changeData) ? this.changeData.meta : {}
    },
    totalItems () {
      return this.dataMeta.total_items || 0
    }
  },

  watch: {
    // Observe changes invoked by the data-table component (e.g. pagination, sorting)
    dataOptions: {
      handler () {
        this.queryData()
      },
      deep: true
    }
  },

  methods: {
    // Refresh action to call from parent components
    refresh () {
      this.queryData()
    },

    // Display a date
    formatDate (date) {
      return (date) ? this.$root.utils.dateFormat(date, 'MMMM D, YYYY') : ''
    },

    // Generate link data for location page
    generateLocationPageLink (item) {
      if (item && item.model) {
        const linkData = {
          name: `${this.locationType}-item`,
          params: {}
        }
        linkData.params[`${this.locationType}Id`] = item.id
        return linkData
      } else {
        return ''
      }
    },

    // Dismisses a notification (changed license record)
    async dismissNotification (item) {
      if (!item || !item.id) return false

      try {
        await this.$store.dispatch('sh/dataApi', {
          method: 'post',
          uri: `${this.locationType}-license-change/${item.id}/dismiss`
        })

        // Reload data
        this.queryData()
      } catch (err) {
        this.dismissError = err.message || err.text || err
      }
    },

    // Perform query of change records
    async queryData () {
      this.isDataLoading = true

      try {
        // Build sort
        // NOTE - Currently enabled for single field sort only !!!
        let orderBy = null
        const sortBy = this.dataOptions.sortBy
        const sortDesc = this.dataOptions.sortDesc
        if (sortBy && sortBy.length > 0) {
          const isDesc = (sortDesc && sortDesc.length > 0) ? sortDesc[0] : false
          const prefix = (isDesc) ? '-' : ''
          orderBy = `${prefix}${sortBy[0]}`
        }

        // Build pagination
        const page = this.dataOptions.page || 1
        const limit = this.dataOptions.itemsPerPage
        const skip = (page === 1) ? 0 : (page - 1) * limit

        // Build request input
        const params = {
          skip,
          limit,
          with: `${this.locationType},approved_licenses_record`
        }

        // Apply sorting
        if (orderBy) params.order_by = orderBy

        // Retrieve results
        this.changeData = await this.$store.dispatch('sh/dataApi', {
          method: 'get',
          uri: `${this.locationType}-license-changes`,
          opts: { params }
        })
      } catch (err) {
        this.dataLoadingError = err.message || err.text || err
      } finally {
        this.isDataLoading = false
      }
    }
  }
}
</script>

<style>
  .location-license-change-list-table .v-data-table > .v-data-table__wrapper > table > tbody > tr > td,
  .location-license-change-list-table .v-data-table > .v-data-table__wrapper > table > thead > tr > td,
  .location-license-change-list-table .v-data-table > .v-data-table__wrapper > table > tfoot > tr > td {
    height: 65px;
  }

  .location-license-change-list-table .v-data-table a {
    text-decoration: none;
  }
  .location-license-change-list-table .v-data-table a:hover {
    text-decoration: underline;
  }
</style>
